<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/External_Components';
    let nextLink= '/Examples/External_Components/An_Icon_Component';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="the-courseschedule-language-1"
  bind:intersecting={visibleById["the-courseschedule-language-1"]}
  onIntersectChange={(visible) => setVisible("the-courseschedule-language-1", visible)}
>The CourseSchedule Language</SectionComponent>
<p>The <strong>CourseSchedule</strong> DSL focuses on scheduling adult training courses.<br>
Its core concepts are <strong>courses</strong>, <strong>rooms</strong>, and <strong>teachers</strong>, with a simplified notion of time:<br>
ten time slots per week—<strong>Monday morning</strong> through <strong>Friday afternoon</strong>.</p>
<p>Below is the metamodel, which by now should feel familiar.</p>
<pre class="language-freon"><code class="language-freon">// CourseSchedule/phase1/defs/main.ast

language CourseSchedule

model CourseSchedule &#123;
    persons: Staff[];
    courses: CourseCategory[];
    rooms: Building[];
    schedule: Schedule[];
&#125;

modelunit Schedule &#123;
    name: identifier;
    timeSlots: Slot[];
    file-extension = "scd";     // the file extension used by the parser
&#125;

modelunit Staff &#123;
    name: identifier;
    teachers: Person[];
    file-extension = "prs";     // the file extension used by the parser
&#125;

modelunit CourseCategory &#123;
    courses: Course[];
    file-extension = "crs";     // the file extension used by the parser
&#125;

modelunit Building &#123;
    rooms: Room[];
    file-extension = "bld";     // the file extension used by the parser
&#125;

/* model unit Schedule */
concept Slot &#123;
    time: TimeStamp;
    reference teacher: Person;
    reference room: Room;
    reference course: Course;
&#125;

limited TimeStamp &#123;
    day: number; // 1 = Monday, 2 = Tuesday, etc
    part: number; // 1 indicates morning, 2 indicates afternoon
    MondayMorning = &#123; day: 1, part: 1 &#125;
    TuesdayMorning = &#123; day: 2, part: 1 &#125;
    WednesdayMorning = &#123; day: 3, part: 1 &#125;
    ThursdayMorning = &#123; day: 4, part: 1 &#125;
    FridayMorning = &#123; day: 5, part: 1 &#125;
    MondayAfternoon = &#123; day: 1, part: 2 &#125;
    TuesdayAfternoon = &#123; day: 2, part: 2 &#125;
    WednesdayAfternoon = &#123; day: 3, part: 2 &#125;
    ThursdayAfternoon = &#123; day: 4, part: 2 &#125;
    FridayAfternoon = &#123; day: 5, part: 2 &#125;
&#125;

/* model unit Staff */
concept Person &#123;
    name: identifier;
    fullName: string;
    phone: number;
    availability: TimeStamp[];
    reference competence: Course[];
&#125;

/* model unit CourseCategory */
concept Course &#123;
    name: identifier;
    description: string;
&#125;

/* model unit AllCourses */
concept Room &#123;
    name: identifier;
    equipment: string;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="the-freon-native-editor-2"
  bind:intersecting={visibleById["the-freon-native-editor-2"]}
  onIntersectChange={(visible) => setVisible("the-freon-native-editor-2", visible)}
>The Freon Native Editor</SectionComponent>
<p>As a first step we’ll create a <strong>native Freon editor</strong>—kept intentionally simple.<br>
We’ll extract a <strong>fragment</strong> for the teacher’s name and phone info, display a teacher’s <strong>competences</strong> as a <strong>checkbox</strong> list, and tidy up the rest.</p>
<pre class="language-freon"><code class="language-freon">// CourseSchedule/phase1/defs/main.edit

editor default

global &#123;
    external &#123;
        PersonIcon,
        PhoneButton,
        StaffAccordion,
        Schedule
    &#125;
&#125;

/* model unit Staff */
Staff &#123;[
Staff in the category: $&#123;self.name&#125;

    $&#123;self.teachers&#125;
]&#125;

Person &#123;[
    [fragment name]
        Availability: $&#123;self.availability checkbox&#125; Competence: $&#123;self.competence&#125;
]
fragment name [
Nickname: $&#123;self.name&#125;
Full Name: $&#123;self.fullName&#125;
Phone number: $&#123;self.phone&#125;
]
&#125;

/* model unit CourseCategory */
CourseCategory &#123;[
Category: $&#123;self.name&#125;

    $&#123;self.courses&#125;
]&#125;

/* model unit Schedule */
Schedule &#123;[
Schedule $&#123;self.name&#125;

    $&#123;self.timeSlots&#125;
]&#125;
Slot &#123;[
$&#123;self.time&#125;
    Teacher: $&#123;self.teacher&#125;
    Room:    $&#123;self.room&#125;
    Course:  $&#123;self.course&#125;
]&#125;</code></pre>
<p>The editor for the <strong>Staff</strong> model unit now looks like this:</p>
<Figure
imageName={'examples/CourseSchedule/Screenshot-step1.png'}
caption={'The native Freon editor'}
figureNumber={1}
/>


<PrevNextSection {prevLink} {nextLink} />